import * as d3 from 'd3'
import Chart from '../../ultils/Chart'
import emptyThumb from '../../assets/images/emptydata.svg'
import './styles.css'
import d3tip from '../../ultils/d3-tip'
import {
  countYAxisTick,
  formatPercentageNumber,
  FUNDAMENTAL_TOGGLE_COLOR,
} from '../../ultils/chart-d3-utils'
import moment from 'moment'

const CREDIT_RISK = 1
const NORMAL = 2
const POD_BOTTOM_PADDING = 6

class PodChart extends Chart {
  init({ data, relations, setSelection, lineKeys, inModal, chartId }) {
    let vis = this
    vis.data = data
    vis.toolTipMode = NORMAL
    vis.lineKeys = lineKeys
    vis.relations = relations
    vis.setSelection = setSelection

    vis.inModal = inModal
    vis.chartId = chartId

    vis.parseTime = d3.timeParse('%d/%m/%Y')
    vis.formatTime = d3.timeFormat('%d/%m/%Y')
    vis.bisectDate = d3.bisector(d => d.date).left

    vis.g
      .append('path')
      .attr('class', 'line1')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line2')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line3')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line4')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line5')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line6')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.x = d3.scaleTime()
    vis.y = d3.scaleLinear()
    vis.yEquity = d3.scaleLinear()

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(inModal ? 8 : 2)
      .tickSizeOuter(0)
    vis.yAxisCall = d3
      .axisLeft()
      .ticks(inModal ? 6 : 4)
      .tickSizeOuter(0)

    vis.xAxis = vis.g
      .append('g')
      .attr('class', 'x axisPoDChart')
      .attr('transform', `translate(0, ${vis.HEIGHT})`)
    vis.yAxis = vis.g
      .append('g')
      .attr('class', 'y axisPoDChart companyHistoryColor')

    vis.focus = vis.g
      .append('g')
      .attr('class', 'focus')
      .style('display', 'none')

    vis.yLegend = vis.svg
      .append('text')
      .attr('class', inModal ? 'axis-label label-in-modal' : 'axis-label')
      .attr('text-anchor', 'end')
      .attr('x', inModal ? 110 : 50)
      .attr('y', 10)
    vis.createToolTip()
    vis.update({ data, lineKeys })
  }

  formatEquityNumber(number) {
    return !isNaN(number) ? Number(number).toFixed(2) : number
  }

  formatNumber(number) {
    const vis = this
    const x = vis.formatAbbreviation(number)
    return !isNaN(number) ? `$${x}` : 'N/A'
  }

  formatAbbreviation(x) {
    const formatSi = d3.format('.3s')
    const s = formatSi(x)
    switch (s[s.length - 1]) {
      case 'G':
        return `${s.slice(0, -1)}B` // billions
      case 'k':
        return `${s.slice(0, -1)}K` // thousands
      case 'M':
        return `${s.slice(0, -1)}M` // thousands
    }
    return s
  }

  createToolTip() {
    const vis = this
    const inModal = vis.inModal ? '-inModal' : '-notInModal'
    d3.selectAll(`.line-chart${vis.chartId}${inModal}`).remove()
    vis.tip = d3tip()
      .attr('class', vis.getTooltipCss(inModal))
      .html((e, d) => vis.generateTooltipHtml(d))
    vis.g.call(vis.tip)
  }

  getTooltipCss(inModal) {
    const vis = this
    return `d3-tip-pod-chart d3-credit-line-chart line-chart${vis.chartId}${inModal}`
  }

  generateTooltipHtml(data) {
    const vis = this
    const listCSMForSort = []
    let returnDiv = `<div class="tip-content">`
    if (vis.toolTipMode === CREDIT_RISK) {
      if (
        data.pod &&
        data.signal_data_array[0] &&
        data.signal_data_array[0].output_change < 0 &&
        data.signal_data_array[0].risk_level != 0
      ) {
        returnDiv += `<div class="tip-header">
                          <p class="tip-date">${moment(data.date).format(
                            'MMM, YYYY',
                          )}</p>
                           <p class="line-chart-row-title">${
                             data.signal_overview
                           }</p>
                      </div>`
      }
    }

    if (data.hasOwnProperty(vis.getLineKeyValue('pod'))) {
      listCSMForSort.push({
        name: 'CSM of ' + data?.proper_name,
        value: formatPercentageNumber(data[vis.getLineKeyValue('pod')]),
        color: FUNDAMENTAL_TOGGLE_COLOR.COMPANY_HISTORY_FC,
        type: 'pod',
      })
    }
    if (data.hasOwnProperty(vis.getLineKeyValue('pod_compare1'))) {
      listCSMForSort.push({
        name: 'CSM of ' + data?.pod_compare1_name,
        value: formatPercentageNumber(
          data[vis.getLineKeyValue('pod_compare1')],
        ),
        color: FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE1,
        type: 'pod_compare1',
      })
    }
    if (data.hasOwnProperty(vis.getLineKeyValue('pod_compare2'))) {
      listCSMForSort.push({
        name: 'CSM of ' + data?.pod_compare2_name,
        value: formatPercentageNumber(
          data[vis.getLineKeyValue('pod_compare2')],
        ),
        color: FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE2,
        type: 'pod_compare2',
      })
    }
    if (data.hasOwnProperty(vis.getLineKeyValue('pod_compare3'))) {
      listCSMForSort.push({
        name: 'CSM of ' + data?.pod_compare3_name,
        value: formatPercentageNumber(
          data[vis.getLineKeyValue('pod_compare3')],
        ),
        color: FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE3,
        type: 'pod_compare3',
      })
    }

    if (data.hasOwnProperty(vis.getLineKeyValue('peer_pod'))) {
      listCSMForSort.push({
        name: 'Default Peer Comparison – Credit Stress Measure',
        value: formatPercentageNumber(data[vis.getLineKeyValue('peer_pod')]),
        color: FUNDAMENTAL_TOGGLE_COLOR.PEER_GROUP_FC,
        type: 'peer_pod',
      })
    }

    if (data.hasOwnProperty(vis.getLineKeyValue('equity_price'))) {
      listCSMForSort.push({
        name: 'Equity Price',
        value: vis.formatEquityNumber(
          data[vis.getLineKeyValue('equity_price')],
        ),
        color: FUNDAMENTAL_TOGGLE_COLOR.EQUITY_PRICE,
        type: 'equity_price',
      })
    }

    listCSMForSort
      .sort((a, b) => b?.value - a?.value)
      .forEach(it => {
        returnDiv += `<div class="row">
                <div class="line-chart-row-title">
                    <span class="dot" style="background-color: ${
                      it?.color
                    }"></span>  
                   ${it?.name} : </div>
                       <div class="line-chart-row-content">${it?.value} ${
          it?.type !== 'equity_price' ? '%' : ''
        }</div>
              </div>`
      })

    return returnDiv
  }

  getImpliedValue(data) {
    const vis = this
    if (vis.toolTipMode === NORMAL) {
      return data.implied_rating
    }
    return vis.impliedRatingValue
  }

  formatTitle(str) {
    return str.replace(/^\w/, function ($0) {
      return $0.toUpperCase()
    })
  }

  countCyValue(d) {
    const vis = this
    const bisect = d3.bisector(d => d.date).left
    const i = bisect(vis.data, d.date)
    const val = vis.data[i]

    if (!val) return null

    return vis.y(val.pod)
  }

  mousemove(e) {
    let vis = this
    if (vis.data.length > 0) {
      const x0 = vis.x.invert(d3.pointer(e)[0] - vis.MARGIN.LEFT)
      const i = vis.bisectDate(vis.data, x0, 1, vis.data.length - 1)
      const d0 = vis.data[i - 1]
      const d1 = vis.data[i]
      const d = x0 - d0.date > d1.date - x0 ? d1 : d0

      // if (d[vis.getLineKeyValue('pod')] == 0) return
      // if (
      //   (d.hasOwnProperty(vis.getLineKeyValue('pod')) &&
      //     d[vis.getLineKeyValue('pod')] === 0) ||
      //   (d.hasOwnProperty(vis.getLineKeyValue('peer_pod')) &&
      //     d[vis.getLineKeyValue('peer_pod')] === 0)
      // ) {
      //   return
      // }

      if (vis.toolTipMode === NORMAL) {
        vis.focus.selectAll('.x-hover-line').remove()
        vis.focus
          .append('line')
          .attr('class', 'x-hover-line hover-line')
          .attr('y1', 0)
          .attr('y2', vis.HEIGHT)
        vis.focus
          .select('.x-hover-line')
          .attr('x1', vis.x(d.date))
          .attr('x2', vis.x(d.date))

        vis.focus.selectAll('circle').remove()
        if (d.hasOwnProperty(vis.getLineKeyValue('pod'))) {
          vis.focus
            .append('circle')
            .attr('class', 'companyHistory')
            .attr('r', 4)
            .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.COMPANY_HISTORY_FC)

          vis.focus
            .select('.companyHistory')
            .attr('cx', vis.x(d.date))
            .attr('cy', vis.y(d[vis.getLineKeyValue('pod')]))
        }

        if (d.hasOwnProperty(vis.getLineKeyValue('pod_compare1'))) {
          vis.focus
            .append('circle')
            .attr('class', 'podCompare1')
            .attr('r', 4)
            .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE1)

          vis.focus
            .select('.podCompare1')
            .attr('cx', vis.x(d.date))
            .attr('cy', vis.y(d[vis.getLineKeyValue('pod_compare1')]))
        }

        if (d.hasOwnProperty(vis.getLineKeyValue('pod_compare2'))) {
          vis.focus
            .append('circle')
            .attr('class', 'podCompare2')
            .attr('r', 4)
            .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE2)

          vis.focus
            .select('.podCompare2')
            .attr('cx', vis.x(d.date))
            .attr('cy', vis.y(d[vis.getLineKeyValue('pod_compare2')]))
        }

        if (d.hasOwnProperty(vis.getLineKeyValue('pod_compare3'))) {
          vis.focus
            .append('circle')
            .attr('class', 'podCompare3')
            .attr('r', 4)
            .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE3)

          vis.focus
            .select('.podCompare3')
            .attr('cx', vis.x(d.date))
            .attr('cy', vis.y(d[vis.getLineKeyValue('pod_compare3')]))
        }

        if (d.hasOwnProperty(vis.getLineKeyValue('peer_pod'))) {
          vis.focus
            .append('circle')
            .attr('class', 'peerGroup')
            .attr('r', 4)
            .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.PEER_GROUP_FC)
          vis.focus
            .select('.peerGroup')
            .attr('cx', vis.x(d.date))
            .attr('cy', vis.y(d[vis.getLineKeyValue('peer_pod')]))
        }

        if (d.hasOwnProperty(vis.getLineKeyValue('equity_price'))) {
          vis.focus
            .append('circle')
            .attr('class', 'equity')
            .attr('r', 4)
            .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.EQUITY_PRICE)
          vis.focus
            .select('.equity')
            .attr('cx', vis.x(d.date))
            .attr('cy', vis.yEquity(d[vis.getLineKeyValue('equity_price')]))
        }
      } else {
        vis.focus.selectAll('circle').remove()
        vis.focus.selectAll('.x-hover-line').remove()
      }

      vis.tip.show(e, d, 10, -d3.pointer(e)[1] - 2)
    }
  }

  getLineKeyValue(key) {
    const vis = this
    const index = vis.lineKeys[vis.lineKeys.indexOf(key)]
    return index
  }

  getCreditScale(property1, property2) {
    const vis = this
    const creditKeys = [
      property1,
      property2,
      'pod_compare1',
      'pod_compare2',
      'pod_compare3',
    ]
    const values = []

    vis.data.forEach(d => {
      creditKeys.forEach(key => {
        const lineKey = vis.getLineKeyValue(key)
        if (!lineKey || !d.hasOwnProperty(lineKey)) return

        const value = Number(d[lineKey])
        if (!Number.isFinite(value) || value < 0) return

        values.push(value)
      })
    })

    const range = values.length ? d3.extent(values) : [0, 1]

    // to draw the line at the bottom and hugging xAxis instead of the middle if all below 0.1%
    if (range[0] === 0.001 && range[1] === 0.001) {
      range[1] = 0.002
    } else if (range[0] === 0 && range[1] === 0) {
      range[1] = 0.001
    } else if (range[1] < 0.01) {
      range[1] *= 5
    }

    return range
  }

  getEquityScale() {
    const vis = this
    const max =
      d3.max(vis.data, d => d[vis.getLineKeyValue('equity_price')]) * 1.05
    const min =
      d3.min(vis.data, d => d[vis.getLineKeyValue('equity_price')]) / 1.05
    return [min, max]
  }

  getXRange() {
    const vis = this
    if (
      !vis.data[0].hasOwnProperty(vis.getLineKeyValue('pod')) &&
      !vis.data[0].hasOwnProperty(vis.getLineKeyValue('peer_pod'))
    ) {
      return d3.extent(vis.data, d => d.date)
    }
    if (
      vis.data[0].hasOwnProperty(vis.getLineKeyValue('pod')) &&
      vis.data[0].hasOwnProperty(vis.getLineKeyValue('peer_pod'))
    ) {
      const lst = vis.data.filter(
        d =>
          d[vis.getLineKeyValue('pod')] >= 0 &&
          d[vis.getLineKeyValue('peer_pod')] >= 0,
      )
      return d3.extent(lst, d => d.date)
    }
    if (vis.data[0].hasOwnProperty(vis.getLineKeyValue('pod'))) {
      const lst = vis.data.filter(d => d[vis.getLineKeyValue('pod')] >= 0)
      return d3.extent(lst, d => d.date)
    }
    if (vis.data[0].hasOwnProperty(vis.getLineKeyValue('peer_pod'))) {
      const lst = vis.data.filter(d => d[vis.getLineKeyValue('peer_pod')] >= 0)
      return d3.extent(lst, d => d.date)
    }
  }

  getEventColor(d) {
    if (!d['signal_data_array'] || d['signal_data_array'].length === 0) {
      return '#1D3967'
    }
    const riskLevel = d['signal_data_array'][0]['risk_level']
    const colorEventScale = d3
      .scaleLinear()
      .domain([0, -1, -2, -3])
      .range(['#758396', '#FF5C5C', '#D10000', '#800202'])
    return colorEventScale(riskLevel)
  }

  addEventCircles() {
    const vis = this
    vis.g.selectAll('.creditRiskData').remove()
    const listCreditRiskData = vis.data.filter(
      d =>
        d.pod >= 0 &&
        d.signal_data_array[0] &&
        d.signal_data_array[0].output_change < 0 &&
        d.signal_data_array[0].risk_level != 0,
    )
    listCreditRiskData.length > 0 &&
      vis.g
        .selectAll('.creditRiskData')
        .data(listCreditRiskData, d => d.id)
        .join(
          enter =>
            enter
              .append('circle')
              .attr('class', 'creditRiskData')
              .attr('cx', d => vis.x(d.date))
              .attr('cy', d => vis.countCyValue(d))
              .attr('r', '5')
              .attr('fill', d => vis.getEventColor(d))
              .style('stroke', 'white')
              .style('stroke-width', '1px')
              .style('opacity', 1)
              .on('mousemove', (e, d) => (vis.toolTipMode = CREDIT_RISK))
              .on('mouseout', () => (vis.toolTipMode = NORMAL)),
          update => {
            update.attr('cx', d => vis.x(d.date))
            update.attr('cy', d => vis.countCyValue(d))
          },
          exit => exit.remove(),
        )
  }

  update({ data, lineKeys }) {
    let vis = this
    vis.data = data || vis.data
    vis.lineKeys = lineKeys || vis.lineKeys
    vis.t = d3.transition().duration(1000)
    vis.svg.selectAll('image').remove()

    if (vis.data.length <= 1) {
      // treat 1 record as empty state to clear xAxis & yAxis value
      vis.data = []
      vis.svg
        .append('svg:image')
        .attr('xlink:href', emptyThumb)
        .attr('x', vis.WIDTH / 2 - 77 + vis.MARGIN.LEFT)
        .attr('y', vis.HEIGHT / 2 - 37 + vis.MARGIN.TOP)
        .attr('width', '155')
        .attr('height', '73')
      vis.yLegend.text('')
    }

    // getting range value for xAxis based on pod & peer_pod value]
    vis.xRange = vis.getXRange()
    vis.range = vis.getCreditScale('pod', 'peer_pod')
    vis.equityRange = vis.getEquityScale()

    vis.x.range([0, vis.WIDTH]).domain(vis.xRange)
    vis.y
      .range([Math.max(vis.HEIGHT - POD_BOTTOM_PADDING, 0), 0])
      .domain(vis.range)
    vis.yEquity.range([vis.HEIGHT, 0]).domain(vis.equityRange)

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(countYAxisTick(vis.data))
      .tickSizeOuter(0)
    vis.xAxisCall.scale(vis.x)
    vis.xAxis.transition(vis.t).call(vis.xAxisCall)

    // if toggle-off both pod & peer_pod
    if (
      vis.data[0].hasOwnProperty(vis.getLineKeyValue('pod')) ||
      vis.data[0].hasOwnProperty(vis.getLineKeyValue('peer_pod'))
    ) {
      vis.yAxisCall.scale(vis.y)
      vis.yAxisCall.tickFormat(function (x) {
        return `${formatPercentageNumber(x)}%`
      })
    } else if (
      vis.data[0].hasOwnProperty(vis.getLineKeyValue('equity_price'))
    ) {
      // vis.yAxisCall.scale(vis.y)
      // vis.yAxisCall.tickFormat(function (x) {
      //   return ''
      // })
      vis.yAxisCall.scale(vis.yEquity)
      vis.yAxisCall.tickFormat(function (x) {
        return x
      })
    } else {
      vis.yAxisCall.tickFormat(function (x) {
        return ''
      })
    }
    vis.yAxis.transition(vis.t).call(vis.yAxisCall)

    vis.equityLine = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(
        d =>
          !isNaN(d[vis.getLineKeyValue('equity_price')]) &&
          d.date >= vis.xRange[0] &&
          d.date <= vis.xRange[1],
      )
      .x(d => vis.x(d.date))
      .y(d => vis.yEquity(d[vis.getLineKeyValue('equity_price')]))

    vis.g
      .select('.line3')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.EQUITY_PRICE)
      .attr('opacity', 0.9)
      .transition(vis.t)
      .attr('d', vis.equityLine(vis.data))

    vis.companyLine = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(
        d =>
          !isNaN(d[vis.getLineKeyValue('pod')]) &&
          d.date >= vis.xRange[0] &&
          d.date <= vis.xRange[1] &&
          d[vis.getLineKeyValue('pod')] >= 0,
      )
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.lineKeys[0]]))

    vis.g
      .select('.line1')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.COMPANY_HISTORY_FC)
      .transition(vis.t)
      .attr('d', vis.companyLine(vis.data.filter(d => d.pod >= 0)))

    vis.podCompare1Line = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(
        d =>
          !isNaN(d[vis.getLineKeyValue('pod_compare1')]) &&
          d.date >= vis.xRange[0] &&
          d.date <= vis.xRange[1] &&
          d[vis.getLineKeyValue('pod_compare1')] >= 0,
      )
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.getLineKeyValue('pod_compare1')]))

    vis.g
      .select('.line4')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE1)
      .transition(vis.t)
      .attr('d', vis.podCompare1Line(vis.data.filter(d => d.pod_compare1 >= 0)))

    vis.podCompare2Line = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(
        d =>
          !isNaN(d[vis.getLineKeyValue('pod_compare2')]) &&
          d.date >= vis.xRange[0] &&
          d.date <= vis.xRange[1] &&
          d[vis.getLineKeyValue('pod_compare2')] >= 0,
      )
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.getLineKeyValue('pod_compare2')]))

    vis.g
      .select('.line5')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE2)
      .transition(vis.t)
      .attr('d', vis.podCompare2Line(vis.data.filter(d => d.pod_compare2 >= 0)))

    vis.podCompare3Line = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(
        d =>
          !isNaN(d[vis.getLineKeyValue('pod_compare3')]) &&
          d.date >= vis.xRange[0] &&
          d.date <= vis.xRange[1] &&
          d[vis.getLineKeyValue('pod_compare3')] >= 0,
      )
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.getLineKeyValue('pod_compare3')]))

    vis.g
      .select('.line6')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.POD_COMPARE3)
      .transition(vis.t)
      .attr('d', vis.podCompare3Line(vis.data.filter(d => d.pod_compare3 >= 0)))

    vis.peerLine = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(
        d =>
          !isNaN(d[vis.getLineKeyValue('peer_pod')]) &&
          d.date >= vis.xRange[0] &&
          d.date <= vis.xRange[1] &&
          d[vis.getLineKeyValue('peer_pod')] >= 0,
      )
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.getLineKeyValue('peer_pod')]))

    vis.g
      .select('.line2')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.PEER_GROUP_FC)
      .attr('opacity', 0.9)
      .transition(vis.t)
      .attr('d', vis.peerLine(vis.data.filter(d => d.peer_pod >= 0)))

    // vis.addEventCircles()

    vis.svg
      .on('mousemove', e => vis.mousemove(e))
      .on('mouseover', () => {
        // vis.tip.hide()
        vis.focus.style('display', null)
        !vis.inModal && vis.setSelection(vis.relations)
      })
      .on('mouseout', () => {
        vis.tip.hide()
        vis.focus.style('display', 'none')
        !vis.inModal && vis.setSelection(null)
      })
  }
}

export default PodChart
